import { useCallback, useRef, type Dispatch, type SetStateAction } from "react";
import type { FooterTerminalLine } from "./use-footer-terminal";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

export function useFooterTerminalProcess(
  setHistory: Dispatch<SetStateAction<FooterTerminalLine[]>>,
  setCwd: Dispatch<SetStateAction<string>>,
) {
  const activeProcess = useRef<{ processId: string; es: EventSource } | null>(
    null,
  );
  const run = useCallback(
    (command: string) => {
      fetch(`${API_BASE}/api/terminal/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ command }),
      })
        .then((response) => response.json())
        .then(
          (data: {
            ok?: boolean;
            processId?: string;
            cwd?: string;
            error?: string;
          }) => {
            if (!data.ok || !data.processId) {
              setHistory((history) => [
                ...history,
                {
                  type: "output",
                  text: data.error ?? "Failed to start command",
                },
              ]);
              return;
            }
            if (data.cwd) setCwd(data.cwd);
            const es = new EventSource(
              `${API_BASE}/api/terminal/stream/${data.processId}`,
            );
            activeProcess.current = { processId: data.processId, es };
            const onData = (event: MessageEvent) => {
              const lines = stripAnsi(String(event.data))
                .split("\n")
                .filter(Boolean);
              setHistory((history) => [
                ...history,
                ...lines.map((text) => ({ type: "output" as const, text })),
              ]);
            };
            es.addEventListener("stdout", onData);
            es.addEventListener("stderr", onData);
            es.addEventListener("exit", (event: MessageEvent) => {
              let exitValue: string | number = "?";
              try {
                const result = JSON.parse(event.data) as {
                  code?: number;
                  signal?: string;
                };
                exitValue = result.code ?? result.signal ?? "?";
              } catch {
                exitValue = "?";
              }
              setHistory((history) => [
                ...history,
                { type: "output", text: `[exit ${exitValue}]` },
              ]);
              es.close();
              activeProcess.current = null;
            });
            es.onerror = () => {
              es.close();
              activeProcess.current = null;
            };
          },
        )
        .catch(() =>
          setHistory((history) => [
            ...history,
            {
              type: "output",
              text: "Backend unavailable, start it with `fintheon start`.",
            },
          ]),
        );
    },
    [setCwd, setHistory],
  );
  const kill = useCallback(() => {
    const active = activeProcess.current;
    if (!active) return;
    void fetch(`${API_BASE}/api/terminal/kill/${active.processId}`, {
      method: "POST",
    });
    active.es.close();
    activeProcess.current = null;
    setHistory((history) => [...history, { type: "output", text: "^C" }]);
  }, [setHistory]);
  return { kill, run };
}

function stripAnsi(value: string): string {
  return value.replace(/\x1b\[[0-9;]*[a-zA-Z]/g, "");
}
